import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface SerialCopyHeaderButtonProps {
  /** Serial values shown on the current page; empties are filtered out
   *  before copying so a partially-serialised page yields a clean list. */
  serials: string[];
}

const stopPropagation = (e: React.SyntheticEvent) => {
  e.stopPropagation();
};

export const SerialCopyHeaderButton: React.FC<SerialCopyHeaderButtonProps> = ({ serials }) => {
  const [copied, setCopied] = useState(false);
  const [errored, setErrored] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFeedbackTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearFeedbackTimer, [clearFeedbackTimer]);

  const count = serials.length;
  const disabled = count === 0;

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (disabled) return;
      try {
        await navigator.clipboard.writeText(serials.join('\n'));
        setCopied(true);
        setErrored(false);
      } catch {
        setCopied(false);
        setErrored(true);
      }
      clearFeedbackTimer();
      timerRef.current = setTimeout(() => {
        setCopied(false);
        setErrored(false);
      }, 1500);
    },
    [serials, disabled, clearFeedbackTimer]
  );

  const tooltipTitle = errored
    ? 'Could not access clipboard'
    : copied
      ? `Copied ${count} ${count === 1 ? 'serial' : 'serials'}`
      : disabled
        ? 'No serials to copy on this page'
        : `Copy ${count} ${count === 1 ? 'serial' : 'serials'} on this page`;

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <span>
        <IconButton
          size="small"
          onClick={handleClick}
          onPointerDown={stopPropagation}
          onMouseDown={stopPropagation}
          disabled={disabled}
          aria-label="Copy all visible serials to clipboard"
          sx={{
            p: 0.25,
            color: 'v4.accent',
            '&.Mui-disabled': { color: 'text.disabled' },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </span>
    </Tooltip>
  );
};
